import React, { useEffect, useState } from 'react';
import { FileText, Trash2, Check, X, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';

export default function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [deleteId, setDeleteId]         = useState(null);
  const [viewItem, setViewItem]         = useState(null);
  const [filter, setFilter]             = useState('all');

  const fetchApps = async () => { setLoading(true); const {data}=await API.get('/applications'); setApplications(data); setLoading(false); };
  useEffect(()=>{ fetchApps(); },[]);

  const updateStatus = async (id, status) => {
    try { await API.put(`/applications/${id}`, { status }); toast.success(status==='approved'?'Application approved!':'Application rejected!'); fetchApps(); }
    catch { toast.error('Could not update status!'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/applications/${deleteId}`); toast.success('Application deleted!'); setConfirmOpen(false); fetchApps(); }
    catch { toast.error('Could not delete!'); }
  };

  const filtered = filter==='all' ? applications : applications.filter(a=>a.status===filter);
  const counts   = { all:applications.length, pending:applications.filter(a=>a.status==='pending').length, approved:applications.filter(a=>a.status==='approved').length, rejected:applications.filter(a=>a.status==='rejected').length };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"><FileText className="text-purple-600" size={22}/></div>
        <div><h1 className="text-xl font-bold text-slate-800">Applications</h1><p className="text-slate-500 text-sm">Manage enrollment applications</p></div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[['all','All','bg-slate-700'],['pending','Pending','bg-yellow-600'],['approved','Approved','bg-green-600'],['rejected','Rejected','bg-red-600']].map(([key,label,color])=>(
          <button key={key} onClick={()=>setFilter(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter===key?`${color} text-white`:'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-4 border-red-700 border-t-transparent"/></div> : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>{['Name','Phone','Course','Batch','Status','Actions'].map(h=><th key={h} className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.length===0
                ? <tr><td colSpan="6" className="text-center py-10 text-slate-400">No applications found</td></tr>
                : filtered.map(a=>(
                  <tr key={a._id} className="hover:bg-slate-50">
                    <td className="py-3 px-4"><p className="font-medium text-slate-800 text-sm">{a.name}</p><p className="text-xs text-slate-400">{a.email}</p></td>
                    <td className="py-3 px-4 text-sm text-slate-600">{a.phone}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{a.courseInterest}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{a.batchRef?.batchName||'—'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${a.status==='pending'?'bg-yellow-100 text-yellow-700':a.status==='approved'?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>
                        {a.status.charAt(0).toUpperCase()+a.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1.5">
                        <button onClick={()=>setViewItem(a)} className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-1.5 rounded-lg transition-colors"><Eye size={13}/></button>
                        {a.status==='pending'&&<>
                          <button onClick={()=>updateStatus(a._id,'approved')} className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded-lg transition-colors"><Check size={13}/></button>
                          <button onClick={()=>updateStatus(a._id,'rejected')} className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-lg transition-colors"><X size={13}/></button>
                        </>}
                        <button onClick={()=>{setDeleteId(a._id);setConfirmOpen(true);}} className="bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-lg transition-colors"><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={!!viewItem} onClose={()=>setViewItem(null)} title="Application Details">
        {viewItem&&(
          <div className="space-y-3 text-sm">
            {[['Name',viewItem.name],['Phone',viewItem.phone],['Email',viewItem.email||'—'],['Address',viewItem.address||'—'],['Course Interest',viewItem.courseInterest],['Batch',viewItem.batchRef?.batchName||'—'],['Message',viewItem.message||'—'],['Applied On',new Date(viewItem.createdAt).toLocaleDateString()]].map(([k,v])=>(
              <div key={k} className="flex gap-3 py-2 border-b border-slate-50 last:border-0">
                <span className="text-slate-500 w-32 flex-shrink-0">{k}:</span>
                <span className="text-slate-800 font-medium">{v}</span>
              </div>
            ))}
            {viewItem.status==='pending'&&(
              <div className="flex gap-3 pt-2">
                <button onClick={()=>{updateStatus(viewItem._id,'approved');setViewItem(null);}} className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm">✓ Approve</button>
                <button onClick={()=>{updateStatus(viewItem._id,'rejected');setViewItem(null);}} className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm">✗ Reject</button>
              </div>
            )}
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={confirmOpen} onConfirm={handleDelete} onCancel={()=>setConfirmOpen(false)}/>
    </div>
  );
}
